import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Copy } from 'lucide-react';
import type { Slide } from '../../../utils/supabase/client';

// ─── Types ────────────────────────────────────────────────────────
interface SlideTypeOption {
  value: string;
  label: string;
  icon: any;
  category: string;
}

interface DraggableSlideStripProps {
  slides: Slide[];
  selectedSlide: number;
  slideTypes: SlideTypeOption[];
  getSlideIcon: (type: string) => any;
  onSelectSlide: (index: number) => void;
  onDuplicateSlide: (index: number) => void;
  onDeleteSlide: (index: number) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
}

// ─── Sortable Mobile Slide Item ───────────────────────────────────
function SortableMobileSlideItem({
  slide,
  index,
  isSelected,
  slideTypes,
  getSlideIcon,
  onSelect,
  onDuplicate,
  onDelete,
}: {
  slide: Slide;
  index: number;
  isSelected: boolean;
  slideTypes: SlideTypeOption[];
  getSlideIcon: (type: string) => any;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const slideTitle = slide.content?.title || 'Untitled';

  return (
    <div
      ref={setNodeRef}
      className={`group relative flex-shrink-0 rounded-lg transition-all ${
        isDragging
          ? 'shadow-lg ring-2 ring-primary/40'
          : isSelected
          ? 'shadow-sm ring-2 ring-primary'
          : 'border border-border hover:border-primary/30'
      }`}
      style={{
        ...style,
        backgroundColor: 'hsl(var(--card))',
      }}
    >
      {/* Left accent for selected */}
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary rounded-l-lg" />
      )}

      {/* Top: drag + slide info */}
      <div className="flex items-center px-1 pt-2 pb-1">
        <button
          className={`flex-shrink-0 w-5 self-stretch flex items-center justify-center cursor-grab active:cursor-grabbing touch-none ${
            isSelected
              ? 'text-primary/60 hover:text-primary'
              : 'text-muted-foreground/30 hover:text-muted-foreground/60'
          }`}
          aria-label={`Drag to reorder slide ${index + 1}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-3 h-3" />
        </button>

        <button
          onClick={onSelect}
          className="flex-1 min-w-0 flex items-center gap-1.5 text-left focus:outline-none px-0.5"
          aria-label={`Slide ${index + 1}: ${slideTitle}`}
          aria-pressed={isSelected}
        >
          <div className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${
            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>
            {index + 1}
          </div>
          <p className={`text-xs font-semibold line-clamp-2 max-w-[90px] ${
            isSelected ? 'text-foreground' : 'text-foreground/80'
          }`} title={slideTitle}>
            {slideTitle}
          </p>
        </button>
      </div>

      {/* Bottom: action buttons — always visible */}
      <div className="flex items-center gap-1 px-1.5 pb-1.5">
        <button
          onClick={onDuplicate}
          className="flex-1 h-6 flex items-center justify-center rounded bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          type="button"
          aria-label={`Duplicate slide ${index + 1}`}
          title="Duplicate"
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={() => {
            if (confirm('Delete this slide?')) {
              onDelete();
            }
          }}
          className="flex-1 h-6 flex items-center justify-center rounded bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
          type="button"
          aria-label={`Delete slide ${index + 1}`}
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─── Mobile Drag Overlay ──────────────────────────────────────────
function MobileDragOverlay({
  slide,
  index,
}: {
  slide: Slide;
  index: number;
}) {
  const slideTitle = slide.content?.title || 'Untitled';

  return (
    <div className="relative flex-shrink-0 rounded-lg overflow-hidden ring-1 ring-primary bg-card shadow-2xl">
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary" />
      <div className="flex items-center gap-0 min-h-11">
        <div className="flex-shrink-0 w-6 self-stretch flex items-center justify-center cursor-grabbing text-primary/60">
          <GripVertical className="w-3 h-3" />
        </div>
        <div className="flex-1 min-w-0 px-1 py-2 flex items-center gap-1.5">
          <div className="flex-shrink-0 w-5 h-5 rounded bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">
            {index + 1}
          </div>
          <p className="text-xs font-semibold text-foreground truncate">{slideTitle}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────
export default function DraggableSlideStrip({
  slides,
  selectedSlide,
  slideTypes,
  getSlideIcon,
  onSelectSlide,
  onDuplicateSlide,
  onDeleteSlide,
  onReorder,
}: DraggableSlideStripProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // slightly larger threshold for mobile touch
      },
    })
  );

  const activeSlide = activeId ? slides.find(s => s.id === activeId) : null;
  const activeIndex = activeId ? slides.findIndex(s => s.id === activeId) : -1;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = slides.findIndex(s => s.id === active.id);
        const newIndex = slides.findIndex(s => s.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onReorder(oldIndex, newIndex);
        }
      }
    },
    [slides, onReorder]
  );

  const slideIds = slides.map(s => s.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={slideIds} strategy={horizontalListSortingStrategy}>
        {slides.map((slide, index) => (
          <SortableMobileSlideItem
            key={slide.id}
            slide={slide}
            index={index}
            isSelected={selectedSlide === index}
            slideTypes={slideTypes}
            getSlideIcon={getSlideIcon}
            onSelect={() => onSelectSlide(index)}
            onDuplicate={() => onDuplicateSlide(index)}
            onDelete={() => onDeleteSlide(index)}
          />
        ))}
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeSlide ? (
          <MobileDragOverlay slide={activeSlide} index={activeIndex} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
